import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Row, Col, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import TicketItem from '../TicketItem';
import extractEventId from '../../utils/helpers';
import SellTicketsHeader from '../SellTicketsHeader';
import SellTicketsLayout from '../SellTicketsLayout';
import withAsyncLifecycle from '../../../core/hoc/async-lifecycle';
import formatFloatWithSuperscript from '../../../core/utils/helpers';
import { selectSellSelectedEvent, selectSellTickets } from '../../selectors/sell-tickets';
import { selectHttpInfo } from '../../../core/selectors/http';
import { saveSelectedTickets } from '../../api/sell';
import {
    deselectAllTickets, getTickets, selectAllTickets, toggleTicketSelection, SELL_GET_TICKETS_REQUEST_NAME,
} from '../../actions/sell-tickets';
import { SELL_STEP_2, SELL_STEP_3 } from '../../constants/steps';

const SellStep2 = () => {
    const dispatch = useDispatch();
    const { isFetching, isError } = useSelector((state) => selectHttpInfo(state, SELL_GET_TICKETS_REQUEST_NAME));

    const tickets = useSelector(selectSellTickets);

    useEffect(() => {
        const eventId = extractEventId();
        dispatch(getTickets(SELL_STEP_2, eventId));
    }, []);

    const SellStep2WithAsyncLifecycle = withAsyncLifecycle(SellStep2Content, {
        isFetching,
        isError,
    });

    return <SellStep2WithAsyncLifecycle tickets={tickets}/>;
};

const SellStep2Content = ({ tickets }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const intl = useIntl();

    const event = useSelector(selectSellSelectedEvent);
    const selectedTickets = tickets.filter((ticket) => ticket.isSelected);
    const allSelected = tickets.every((ticket) => ticket.isSelected);

    const saveTickets = () => {
        const goToNextStep = () => navigate(`/sell/${SELL_STEP_3}`);
        const errorNotify = () => toast.error(intl.formatMessage({ id: 'notify_error_message' }));
        saveSelectedTickets(tickets, goToNextStep, errorNotify);
    };

    const handleToggleAll = () => {
        if (allSelected) {
            dispatch(deselectAllTickets());
        } else {
            dispatch(selectAllTickets());
        }
    };

    return (
        <SellTicketsLayout>
            <Row>
                <Col lg={{ span: 10, offset: 1 }} md={12}>
                    <div className="sell-tickets__header__container">
                        <SellTicketsHeader
                            title={<FormattedMessage id="sell_select_tickets_to_sell"/>}
                            subtitle={<FormattedMessage id="sell_select_tickets_to_sell_desc"/>}
                        />
                        <div className="sell-tickets__header__btn">
                            <Button variant={allSelected ? 'outline-primary' : 'primary'} onClick={handleToggleAll}
                                className="mb-3">
                                {allSelected ? <FormattedMessage id="deselect_all"/> : <FormattedMessage id="select_all"/>}
                            </Button>
                        </div>
                    </div>
                    <div className="tickets-container">
                        {tickets.map((ticket) => (
                            <div className={`ticket-container border rounded mb-3 ${ticket.isSelected ? 'active' : ''}`}
                                key={ticket.code}>
                                <Row>
                                    <Col lg={{ span: 9 }} md={12}>
                                        <TicketItem ticket={ticket} event={event}/>
                                    </Col>
                                    <Col lg={{ span: 3 }} md={12} className="ticket-container-actions">
                                        <div className="event_actions">
                                            <div className="event_old_price_container">
                                                <span className="text-muted"><FormattedMessage
                                                    id="sell_you_paid"/></span>
                                                <span className="event_price fw-bold">
                                                    {formatFloatWithSuperscript(ticket.buyPrice)} {ticket.currency}
                                                </span>
                                                <span className="text-muted"> <FormattedMessage
                                                    id="for_this_ticket"/></span>
                                            </div>
                                            <ToggleButtonGroup type="checkbox">
                                                <ToggleButton
                                                    variant={ticket.isSelected ? 'primary' : 'outline-primary'}
                                                    key={ticket.code}
                                                    checked={ticket.isSelected}
                                                    value={ticket.code}
                                                    onClick={() => dispatch(toggleTicketSelection(ticket.code))}
                                                    id={`toggle-${ticket.code}`}>
                                                    {ticket.isSelected ? <><i className="bi bi-check-lg"></i>
                                                        <FormattedMessage id="selected"/></>
                                                        : <FormattedMessage id="select"/>}
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        ))}
                    </div>
                </Col>
            </Row>
            <Row>
                <Col lg={{ span: 10, offset: 1 }} md={12}>
                    <div className="step-actions d-flex mt-3 mb-5">
                        <Button variant='link' className="text-decoration-none me-2"
                            onClick={() => navigate('/sell/1')}><FormattedMessage id="prev"/> </Button>
                        <Button variant='primary' size="lg" onClick={saveTickets}
                            disabled={!selectedTickets.length}>
                            <FormattedMessage id="continue"/>
                        </Button>
                    </div>
                </Col>
            </Row>
        </SellTicketsLayout>
    );
};
export default SellStep2;
